import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';

import { capitalize } from 'utils/strings';
import { useCart } from 'contexts/Cart';

import { Collapse } from 'react-bootstrap';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import { Component as PlusIcon } from 'icons/icon-ui--plus.svg';
import ProductPackageSku from './ProductPackageSku';
import ProductPackageDetails from './ProductPackageDetails';

type Props = {
  menuHeader?: string;
  menuSubHeader?: string;
  isPDPPackageProduct?: boolean;
  thanksgivingSkus ?: number[]
  allowClose ?: boolean;
};

function ProductPackage({
  menuHeader,
  menuSubHeader,
  isPDPPackageProduct = false,
  thanksgivingSkus,
  allowClose = true,
}: Props): ReactElement {
  const { cart, dispatch } = useCart();
  const {
    id: cartId,
    package_products: packageProducts,
    menu_items: menuItems,
    skus_added_outside_widget: skusAddedOutsideWidget,
    last_added_by_recipe_tile: lastAddedByRecipeTile,
    hasChanged,
    hasError,
  } = cart;
  const skuOrderMap = new Map(cart.package_products.map(
    (packageProduct, index) => [packageProduct.sku_id, index],
  ));
  const filteredMenuItems = thanksgivingSkus
    ? cart.menu_items.filter((item) => thanksgivingSkus.includes(item.sku.id))
    : cart.menu_items.filter((item) => skuOrderMap.has(item.sku.id));
  const removeSoldOutSkus = filteredMenuItems.filter((item) => !item.isSoldOut);
  const packageProductMenuItems = removeSoldOutSkus.sort(
    // @ts-expect-error TODO: fix null safety
    (a, b) => skuOrderMap.get(a.sku.id) - skuOrderMap.get(b.sku.id),
  );

  const initialSkuIds = thanksgivingSkus || packageProducts.map((item) => item.sku_id);
  const [selectedSkuIds, setSelectedSkuIds] = useState(initialSkuIds);
  const [expanded, setExpanded] = useState(
    !packageProductMenuItems.every((item) => item.quantity > 0),
  );
  const toggleExpanded = () => setExpanded(!expanded);
  const [selectedSkuTitlesText, setSelectedSkuTitlesText] = useState('');
  const packageProductsInCart = packageProductMenuItems.filter((item) => item.quantity > 0);
  const allPackageProductsInCart = packageProductMenuItems.every((item) => item.quantity > 0);
  const selectedPackageProducts = menuItems
    .filter((item) => selectedSkuIds
      .includes(item.sku.id) && item.quantity === 0 && !item.isSoldOut);
  const organizedPackageProducts = packageProductMenuItems
    .sort((itemInCart, itemNotInCart) => itemNotInCart.quantity - itemInCart.quantity);
  const packageProductsInCartText = (title) => {
    let lastTitle;
    switch (title.length) {
      case 1:
        return title[0];
      case 2:
        return `${title[0]} and ${title[1]}`;
      default:
        lastTitle = title.pop();
        return `${title.join(', ')}, and ${lastTitle}`;
    }
  };
  const addedByRecipeTile = (skuId) => {
    dispatch({
      type: 'setAddedOutsideWidget',
      skuId,
    });
  };

  useEffect(() => {
    if (expanded) {
      setExpanded(!allPackageProductsInCart);
    }
    packageProductsInCart.forEach((item) => {
      addedByRecipeTile(item.sku.id);
    });
  }, [expanded]);

  useEffect(() => {
    const selectedProductsMessage = skusAddedOutsideWidget.length > 0
      ? `Based on your selection of ${packageProductsInCartText(skusAddedOutsideWidget
      // @ts-expect-error TODO: fix null safety
        .map((item) => item.sku.title))}: `
      : ' ';

    setSelectedSkuTitlesText(selectedProductsMessage);
  }, [skusAddedOutsideWidget.length, expanded]);

  useEffect(() => {
    if (!allPackageProductsInCart) {
      setExpanded(true);
    }
  }, [allPackageProductsInCart]);
  const toggleSelected = (skuId) => setSelectedSkuIds((prevState) => (
    prevState.includes(skuId)
      ? prevState.filter((id) => id !== skuId)
      : [...prevState, skuId]
  ));
  if ((!expanded
      || packageProducts.length <= 1
      || lastAddedByRecipeTile) && thanksgivingSkus?.length === 0) {
    // @ts-expect-error TODO: fix null safety
    return null;
  }

  return (
    <div id="products_package_widget">
      {allowClose && (

        <button
          className="c-product-package__close pb-2"
          type="button"
          aria-label="Close modal"
          onClick={toggleExpanded}
        >
          <CloseIcon width="2rem" height="2rem" />
        </button>
      )}

      <br />

      <div
        className="c-meal-management-recipes-container__title"
        style={{ margin: '0 0 20px 0' }}
      >
        <h2 className="c-heading c-heading--brand-4">
          {menuHeader ? capitalize(menuHeader) : 'Frequently Bought Together'}
        </h2>
        <div>
          { !isPDPPackageProduct
              && selectedSkuTitlesText}
          {menuSubHeader || 'We think you’ll also love these plant-based products'}
        </div>
      </div>

      {allowClose ? (
        <>
          <hr />

          <Collapse in={expanded}>
            <div>
              <div className="row c-product-package__row">
                <div className="c-product-package__container">
                  {organizedPackageProducts.map((cartItem, index) => (
                    <div className="c-product-package__item-container" key={cartItem.sku.id}>
                      <div
                        className={[
                          'c-product-package__skus',
                          cartItem.quantity > 0 ? 'in-cart' : '',
                        ].join(' ')}
                        key={cartItem.sku.id}
                      >
                        <div className="product-package__sku-wrapper">
                          <ProductPackageSku
                            packageProduct={cartItem}
                            selected={selectedSkuIds.includes(cartItem.sku.id)}
                            toggleSelected={() => toggleSelected(cartItem.sku.id)}
                            cartId={cartId}
                            hasCartChanged={hasChanged}
                            hasCartError={hasError}
                            isPDPPackageProduct={isPDPPackageProduct}
                          />
                        </div>
                      </div>
                      {index !== removeSoldOutSkus.length - 1 && (
                        <PlusIcon className="c-product-package__plus-sign" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Collapse>
        </>
      ) : (
        <div className="row c-product-package__row">
          <div className="c-product-package__container" style={{ justifyContent: 'center' }}>
            {organizedPackageProducts.map((cartItem, index) => (
              <div className="c-product-package__item-container" key={cartItem.sku.id}>
                <div
                  className={[
                    'c-product-package__skus',
                    cartItem.quantity > 0 ? 'in-cart' : '',
                  ].join(' ')}
                  key={cartItem.sku.id}
                >
                  <div className="product-package__sku-wrapper">
                    <ProductPackageSku
                      packageProduct={cartItem}
                      selected={selectedSkuIds.includes(cartItem.sku.id)}
                      toggleSelected={() => toggleSelected(cartItem.sku.id)}
                      cartId={cartId}
                      hasCartChanged={hasChanged}
                      hasCartError={hasError}
                      isPDPPackageProduct={isPDPPackageProduct}
                    />
                  </div>
                </div>
                {index !== removeSoldOutSkus.length - 1 && (
                  <PlusIcon className="c-product-package__plus-sign" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <ProductPackageDetails
        selectedPackageProducts={selectedPackageProducts}
        allPackageProductsInCart={allPackageProductsInCart}
        toggleSelected={toggleSelected}
      />
    </div>
  );
}
export default ProductPackage;
